import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useConciergeWidget } from "@/utils/hooks/useConciergeWidget";
import { useIsConciergePage } from "@/utils/hooks/useIsConciergePage";
import { ConciergeWidgetChat } from "./ConciergeWidgetChat";
import { useIsConciergeEnabled } from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";

export const ConciergeWidget: React.FC = () => {
  const { isOpen, closeWidget } = useConciergeWidget();
  const isConciergePage = useIsConciergePage();
  const widgetRef = useRef<HTMLDivElement>(null);
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const isConciergeSplitEnabled = useIsConciergeEnabled({
    email: userProfile?.email,
  });

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeWidget();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeWidget]);

  // Prevent body scroll when widget is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Hide widget when on the concierge page or when not in concierge split
  if (isConciergePage || !isConciergeSplitEnabled) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  const widget = (
    <>
      {/* Backdrop for mobile */}
      <div
        className="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-[1399] md:tw:hidden tw:animate-in tw:fade-in tw:duration-300"
        onClick={closeWidget}
      />

      {/* Widget container */}
      <div
        ref={widgetRef}
        className="concierge-widget-container tw:fixed tw:z-[1400] tw:bg-white tw:shadow-2xl tw:flex tw:flex-col tw:overflow-hidden"
        style={{
          maxHeight: "calc(100vh - 100px)",
        }}
      >
        <ConciergeWidgetChat />
      </div>
      <style>{`
        .concierge-widget-container {
          /* Mobile styles - default */
          width: 100%;
          height: calc(100vh - 80px);
          bottom: 0;
          right: 0;
          left: 0;
          border-radius: 1rem 1rem 0 0;
          animation: slideInFromBottom 0.3s ease-out;
        }

        @media (min-width: 768px) {
          .concierge-widget-container {
            /* Desktop styles - aligned with floating button bottom-right corner */
            width: 400px !important;
            height: 600px !important;
            bottom: 1.5rem !important;
            right: 1.5rem !important;
            left: auto !important;
            border-radius: 1rem !important;
          }
        }

        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );

  // Render widget in a portal at the root level
  return createPortal(widget, document.body);
};
